// PATH: src/pages/admin/tabs/MediaTab.jsx
// VERSION: 2.0 — Multi-upload con CloudinaryMultiImageField + guardar lote

import React, { useEffect, useState } from 'react';
import {
  Box, Button, Flex, Input, Table, Thead, Tbody, Tr, Th, Td, useToast, Heading, Spinner, Select, Text, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiSave, FiPlusCircle, FiEdit2, FiTrash2, FiFolder, FiUploadCloud,
} from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';
import CloudinaryMultiImageField from '../../../admin/components/CloudinaryMultiImageField';



export default function MediaTab() {
  const toast = useToast();

  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const sectionHeadingColor = useColorModeValue('brand.700', 'text.default');
  const batchTextColor = useColorModeValue('gray.600', 'text.muted');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  const bw = {
    box: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.200', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'text.muted'), fontWeight: 'semibold' },
      _hover: { borderColor: useColorModeValue('brand.400', 'brand.300') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: {
        bg: 'accent.600',
        borderColor: 'accent.600',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      },
      _active: {
        transform: 'translateY(0)',
        boxShadow: 'sm',
      },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: useColorModeValue('white', 'whiteAlpha.100'),
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: {
        bg: useColorModeValue('brand.50', 'whiteAlpha.200'),
        borderColor: useColorModeValue('brand.400', 'brand.300'),
      },
    },
    select: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _hover: { borderColor: useColorModeValue('brand.400', 'brand.300') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
  };

  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [items, setItems] = useState([]);

  // Lote temporal de subida (array de { publicId, title, type })
  const [uploadBatch, setUploadBatch] = useState([]);

  const [folderForm, setFolderForm] = useState({ id: null, name: '' });
  const [itemForm, setItemForm] = useState({
    id: null,
    title: '',
    type: 'photo',
    src: '',
    thumbnail: '',
  });

  async function loadFolders() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/media/folders');
      setFolders(data?.items || data || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando carpetas' });
    } finally {
      setLoading(false);
    }
  }

  async function loadItems(folderId) {
    if (!folderId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/media/folders/${folderId}/items`);
      setItems(data?.items || data || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando items' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadFolders(); }, []);
  useEffect(() => {
    if (selectedFolderId) loadItems(selectedFolderId);
    else setItems([]);
  }, [selectedFolderId]);

  function editFolder(f) {
    setFolderForm({ id: f.id, name: f.name });
  }
  function resetFolder() { setFolderForm({ id: null, name: '' }); }

  async function saveFolder() {
    if (!folderForm.name.trim()) {
      toast({ status: 'warning', title: 'Nombre de carpeta requerido' });
      return;
    }
    try {
      if (folderForm.id) {
        await axiosInstance.patch(`/media/folders/${folderForm.id}`, { name: folderForm.name.trim() });
        toast({ status: 'success', title: 'Carpeta actualizada' });
      } else {
        await axiosInstance.post('/media/folders', { name: folderForm.name.trim() });
        toast({ status: 'success', title: 'Carpeta creada' });
      }
      resetFolder();
      loadFolders();
    } catch {
      toast({ status: 'error', title: 'No se pudo guardar la carpeta' });
    }
  }

  async function deleteFolder(id) {
    if (!window.confirm('¿Eliminar carpeta? (Se perderán los items)')) return;
    try {
      await axiosInstance.delete(`/media/folders/${id}`);
      toast({ status: 'success', title: 'Carpeta eliminada' });
      if (String(selectedFolderId) === String(id)) {
        setSelectedFolderId('');
        setItems([]);
      }
      loadFolders();
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  function editItem(it) {
    setItemForm({
      id: it.id,
      title: it.title || '',
      type: it.type || 'photo',
      src: it.src || '',
      thumbnail: it.thumbnail || '',
    });
  }
  function resetItem() { setItemForm({ id: null, title: '', type: 'photo', src: '', thumbnail: '' }); }

  async function saveItem() {
    if (!selectedFolderId) {
      toast({ status: 'warning', title: 'Selecciona una carpeta' });
      return;
    }
    const payload = {
      title: itemForm.title.trim(),
      type: itemForm.type,
      src: itemForm.src.trim(), // si es Cloudinary, guarda publicId
      thumbnail: itemForm.thumbnail || null,
    };
    if (!payload.title || !payload.type || !payload.src) {
      toast({ status: 'warning', title: 'Título, tipo y src son requeridos' });
      return;
    }
    try {
      if (itemForm.id) {
        await axiosInstance.patch(`/media/items/${itemForm.id}`, payload);
        toast({ status: 'success', title: 'Item actualizado' });
      } else {
        await axiosInstance.post(`/media/folders/${selectedFolderId}/items`, payload);
        toast({ status: 'success', title: 'Item creado' });
      }
      resetItem();
      loadItems(selectedFolderId);
    } catch {
      toast({ status: 'error', title: 'No se pudo guardar item' });
    }
  }

  async function deleteItem(id) {
    if (!window.confirm('¿Eliminar item?')) return;
    try {
      await axiosInstance.delete(`/media/items/${id}`);
      toast({ status: 'success', title: 'Item eliminado' });
      loadItems(selectedFolderId);
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar item' });
    }
  }

  async function saveBatchToFolder() {
    if (!selectedFolderId) {
      toast({ status: 'warning', title: 'Selecciona una carpeta' });
      return;
    }
    if (!uploadBatch.length) {
      toast({ status: 'warning', title: 'No hay archivos en el lote' });
      return;
    }
    setLoading(true);
    try {
      for (const it of uploadBatch) {
        await axiosInstance.post(`/media/folders/${selectedFolderId}/items`, {
          title: it.title || 'Sin título',
          type: it.type === 'video' ? 'video' : 'photo',
          src: it.publicId, // publicId Cloudinary
          thumbnail: null,
        });
      }
      toast({ status: 'success', title: `Se guardó el lote (${uploadBatch.length})` });
      setUploadBatch([]);
      loadItems(selectedFolderId);
    } catch {
      toast({ status: 'error', title: 'Error guardando el lote' });
    } finally {
      setLoading(false);
    }
  }

  const selectedFolder = folders.find((f) => String(f.id) === String(selectedFolderId));

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>Media</Heading>
      <Box fontSize="sm" mb={4} color={subTextColor}>
        Organiza carpetas de fotos y videos. Puedes subir múltiples archivos y luego guardarlos como items de una carpeta.
      </Box>

      <Heading fontSize="md" mb={2} color={sectionHeadingColor}>Carpetas</Heading>
      <Flex gap={2} mb={2} wrap="wrap">
        <Input {...bw.input} placeholder="Nombre de carpeta" value={folderForm.name}
          onChange={(e) => setFolderForm((f) => ({ ...f, name: e.target.value }))} />
        <Button {...bw.btn} leftIcon={folderForm.id ? <FiSave /> : <FiPlusCircle />} onClick={saveFolder}>
          {folderForm.id ? 'Actualizar' : 'Crear'}
        </Button>
        {folderForm.id && <Button {...bw.btnGhost} onClick={resetFolder}>Cancelar</Button>}
        <Button {...bw.btnGhost} leftIcon={<FiRefreshCw />} onClick={loadFolders}>Refrescar</Button>
      </Flex>

      <Flex gap={2} mb={3} wrap="wrap" align="center">
        <Select {...bw.select} placeholder="Selecciona carpeta" value={selectedFolderId}
          onChange={(e) => setSelectedFolderId(e.target.value)} maxW="260px">
          {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </Select>
        {selectedFolderId && (
          <>
            <Button {...bw.btnGhost} leftIcon={<FiFolder />}
              onClick={() => { const folder = folders.find((f) => String(f.id) === String(selectedFolderId)); if (folder) editFolder(folder); }}>
              Editar carpeta
            </Button>
            <Button {...bw.btnGhost} leftIcon={<FiTrash2 />} onClick={() => deleteFolder(selectedFolderId)}>
              Eliminar carpeta
            </Button>
          </>
        )}
      </Flex>

      {/* Multi-upload a Cloudinary */}
      <Box mt={4} mb={3}>
        <CloudinaryMultiImageField
          label={`Subir archivos al lote ${selectedFolder ? `→ carpeta: ${selectedFolder.name}` : ''}`}
          value={uploadBatch}
          onChange={setUploadBatch}
          folderBase="media"
          albumTitle={selectedFolder?.name || 'general'}
        />
        <Flex gap={2} mt={3} wrap="wrap" align="center">
          <Button {...bw.btn} leftIcon={<FiUploadCloud />} onClick={saveBatchToFolder} isDisabled={!uploadBatch.length || !selectedFolderId}>
            Guardar lote en carpeta
          </Button>
          <Text fontSize="sm" color={batchTextColor}>
            Archivos en lote: <b>{uploadBatch.length}</b>
          </Text>
        </Flex>
      </Box>

      <Heading fontSize="md" mb={2} color={sectionHeadingColor}>Items</Heading>
      <Flex gap={2} mb={2} wrap="wrap">
        <Input {...bw.input} placeholder="Título" value={itemForm.title}
          onChange={(e) => setItemForm((s) => ({ ...s, title: e.target.value }))} />
        <Select {...bw.select} value={itemForm.type}
          onChange={(e) => setItemForm((s) => ({ ...s, type: e.target.value }))}>
          <option value="photo">photo</option>
          <option value="video">video</option>
        </Select>
        <Input {...bw.input} placeholder="Src (publicId o URL)" value={itemForm.src}
          onChange={(e) => setItemForm((s) => ({ ...s, src: e.target.value }))} />
        <Input {...bw.input} placeholder="Thumbnail (URL opcional)" value={itemForm.thumbnail}
          onChange={(e) => setItemForm((s) => ({ ...s, thumbnail: e.target.value }))} />
        <Button {...bw.btn} leftIcon={itemForm.id ? <FiSave /> : <FiPlusCircle />} onClick={saveItem}>
          {itemForm.id ? 'Actualizar' : 'Crear'}
        </Button>
        {itemForm.id && <Button {...bw.btnGhost} onClick={resetItem}>Cancelar</Button>}
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: 'brand.200' } }}
            border="1px solid"
            borderColor="brand.200"
          >
            <Thead bg={theadBg}>
              <Tr>
                <Th borderColor="brand.200">ID</Th>
                <Th borderColor="brand.200">Título</Th>
                <Th borderColor="brand.200">Tipo</Th>
                <Th borderColor="brand.200">Src</Th>
                <Th borderColor="brand.200">Thumbnail</Th>
                <Th borderColor="brand.200">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((it) => (
                <Tr key={it.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor="brand.200">{it.id}</Td>
                  <Td borderColor="brand.200">{it.title}</Td>
                  <Td borderColor="brand.200">{it.type}</Td>
                  <Td borderColor="brand.200" maxW="240px" isTruncated>{it.src}</Td>
                  <Td borderColor="brand.200" maxW="220px" isTruncated>{it.thumbnail || ''}</Td>
                  <Td borderColor="brand.200" whiteSpace="nowrap">
                    <Button {...bw.btnGhost} size="xs" mr={2} leftIcon={<FiEdit2 />} onClick={() => editItem(it)}>
                      Editar
                    </Button>
                    <Button {...bw.btnGhost} size="xs" leftIcon={<FiTrash2 />} onClick={() => deleteItem(it.id)}>
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
              {items.length === 0 && selectedFolderId && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={4} borderColor="brand.200">
                    No hay items en esta carpeta.
                  </Td>
                </Tr>
              )}
              {items.length === 0 && !selectedFolderId && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={4} borderColor="brand.200">
                    Selecciona una carpeta para ver sus items.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
